import { useCallback, useEffect, useRef, useState } from 'react'

import CustomEventTypes from '@/utils/analytics/customEventTypes'

import useTriggerEvents from './useTriggerEvents'
// import { trackCustomEvent } from '@/utils/analytics/googleAnalytics'

const useTextToSpeech = (lang: string) => {
  const synthRef = useRef<SpeechSynthesis | null>(null)
  const [isLanguageSupported, setIsLanguageSupported] = useState(true) // Intentionally assuming true until voices are fetched
  const { trackCustomEvent } = useTriggerEvents()

  useEffect(() => {
    if ('speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis
    }

    return () => {
      if (synthRef.current) {
        synthRef.current.cancel()
        synthRef.current.onvoiceschanged = null
      }
    }
  }, [])

  const getVoice = useCallback(() => {
    if (synthRef && synthRef.current) {
      // Hindi voice works for marathi as well as english for most cases
      // Currently for english also, we are serving posts in hindi
      if (lang === 'hi' || lang === 'mr' || lang === 'en') {
        return synthRef.current
          .getVoices()
          .find((voice) => voice.lang.includes('hi'))
      } else {
        const voice = synthRef.current
          .getVoices()
          .find((voice) => voice.lang.includes(lang))

        if (voice) {
          return voice
        } else {
          setIsLanguageSupported(false)
        }
      }
    }
    return undefined
  }, [lang])

  useEffect(() => {
    getVoice()
  }, [getVoice])

  const speak = (
    text: string,
    options = {
      pitch: 1,
      rate: 1,
      volume: 1,
    },
  ) => {
    if (!synthRef.current) {
      return
    }

    if (synthRef.current.speaking && !synthRef.current.paused) {
      trackCustomEvent(CustomEventTypes.TextToSpeechPaused)
      synthRef.current.pause()
    } else if (synthRef.current.paused) {
      trackCustomEvent(CustomEventTypes.TextToSpeechResumed)
      synthRef.current.resume()
    } else {
      trackCustomEvent(CustomEventTypes.TextToSpeechPlayed)
    }

    const { pitch, rate, volume } = options

    const utterance = new SpeechSynthesisUtterance(text)

    const voice = getVoice()

    if (voice) {
      utterance.voice = voice
    } else {
      return
    }

    if (rate) utterance.rate = rate
    if (pitch) utterance.pitch = pitch
    if (volume) utterance.volume = volume

    synthRef.current.speak(utterance)
  }

  return { isLanguageSupported, getVoice, speak }
}

export default useTextToSpeech
